import { formatNumber, mealByTime, POINTS, workoutCounts, type DayNutrition, type Meal, type Workout } from '@jungles/shared';

export interface HomeWorkouts {
  today: string;
  // Server findFirst is over ALL owner-scoped completed history, not the chart's take:8.
  last: Workout | null;
  thisWeek: Workout[];
  draft: Workout | null;
}
export interface QuickCopy { title: string; detail: string; accent?: string; suffix?: string; hot?: boolean }

export function workoutQuickCopy(draft: Workout | null, summary: HomeWorkouts | null, date: string,
  week: { workoutsDone: number; workoutsGoal: number } | null): QuickCopy {
  if (draft) {
    const done = draft.exercises.filter(ex => ex.sets.length > 0 && ex.sets.every(s => s.done || (s.weightKg !== null && s.reps !== null))).length;
    return { title: `Продолжить\n«${draft.title}»`, detail: `${done} из ${draft.exercises.length} упражнений`, hot: true };
  }
  if (!summary || summary.today !== date || !Array.isArray(summary.thisWeek))
    return { title: 'Начать\nтренировку', detail: 'Новая тренировка' };
  const today = summary.thisWeek.filter(w => w.status === 'done' && w.date === date);
  // Preserve the existing reward engine: two short/non-qualifying records do not exhaust it.
  if (today.filter(w => workoutCounts(w.durationMin, w.setsCount)).length >= POINTS.maxWorkoutsPerDay)
    return { title: 'Записать\nтренировку', detail: 'Очки за сегодня ✓' };
  if (today.length) {
    const minutes = today.reduce((n, w) => n + (w.durationMin ?? 0), 0);
    return { title: 'Ещё одна\nтренировка', detail: `Сегодня ✓${minutes > 0 ? ` ${formatNumber(minutes)} мин` : ''}` };
  }
  if (summary.last === null) return { title: 'Первая\nтренировка', detail: 'Соберём за минуту' };
  if (week && Number.isInteger(week.workoutsGoal) && week.workoutsGoal > 0) {
    const progress = `${week.workoutsDone} из ${week.workoutsGoal}`;
    return week.workoutsDone >= week.workoutsGoal
      ? { title: 'Начать\nтренировку', detail: 'Цель недели ', accent: `${progress} ✓` }
      : { title: 'Начать\nтренировку', detail: '', accent: progress, suffix: ' за неделю' };
  }
  return { title: 'Начать\nтренировку', detail: week ? `${week.workoutsDone} за неделю` : 'Новая тренировка' };
}

export function nextHomeMeal(time: string, day: DayNutrition | null): Meal {
  const initial = mealByTime(time);
  if (initial === 'snack' || !day?.meals[initial]?.entries.length) return initial;
  const primary: Meal[] = ['breakfast', 'lunch', 'dinner'];
  const index = primary.indexOf(initial);
  // Next unrecorded primary meal, wrapping only when later primary meals are all present.
  return [...primary.slice(index + 1), ...primary.slice(0, index)].find(m => !day.meals[m]?.entries.length) ?? 'snack';
}
const names: Record<Meal, string> = { breakfast: 'завтрак', lunch: 'обед', dinner: 'ужин', snack: 'перекус' };
export function foodQuickCopy(meal: Meal, day: DayNutrition | null, target: number | null | undefined): QuickCopy {
  const title = `Записать\n${names[meal]}`;
  if (!day) return { title, detail: 'Добавить питание' };
  const kcal = formatNumber(day.kcal);
  if (target != null && target > 0) {
    if (day.kcal > target) return { title, detail: `${kcal} · `, accent: `+${formatNumber(day.kcal - target)}`, suffix: ' к цели' };
    return { title, detail: `${kcal} из ${formatNumber(target)} ккал` };
  }
  return { title, detail: `Сегодня ${kcal} ккал` };
}
